import React, { useState, useRef, useEffect, useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Home,
  Info,
  Zap,
  Phone,
  HelpCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/user-context';

export default function LandingNavbar() {
  const { isLoggedIn } = useAuth();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef(null);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location]);

  // Listen for language changes and force re-render
  useEffect(() => {
    const handleLanguageChanged = () => {
      forceUpdate();
    };

    // Listen to i18n language change event
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const handleLanguageChange = useCallback(
    async (lang) => {
      localStorage.setItem('app-language', lang);
      await i18n.changeLanguage(lang);
      setShowLangDropdown(false);
    },
    [i18n]
  );

  // Click outside to close mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMenu]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowMobileMenu(false);
    }
  };

  // Navigation items
  const navItems = [
    {
      id: 'home',
      label: t('navbar.home'),
      icon: Home,
      color: 'text-orange-500',
    },
    {
      id: 'features',
      label: t('navbar.features'),
      icon: Zap,
      color: 'text-yellow-500',
    },
    {
      id: 'about',
      label: t('navbar.aboutUs'),
      icon: Info,
      color: 'text-blue-500',
    },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b transition-colors ${
        theme === 'dark'
          ? 'bg-[#0B0E14]/80 border-gray-800'
          : 'bg-white/80 border-gray-200'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Brand + Language */}
        <div className="flex items-center gap-3">
          <Link
            to="/landing"
            className={`text-2xl font-bold tracking-tight ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}
          >
            <span className="text-orange-500">{t('navbar.brandColored')}</span>{' '}
            {t('navbar.brandUncolored')}
          </Link>

          {/* Language selector */}
          <div className="relative">
            <button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className={`flex items-center justify-center rounded-lg transition p-1 ${
                theme === 'dark'
                  ? 'bg-gray-800/50 hover:bg-gray-700/50'
                  : 'bg-gray-100/50 hover:bg-gray-200/50'
              }`}
              title={t('common.changeLanguage')}
            >
              <span className="text-sm font-medium px-2 py-1">
                {i18n.language === 'en' ? 'EN' : i18n.language === 'fa' ? 'FA' : 'PS'}
              </span>
            </button>

            {showLangDropdown && (
              <div
                className={`absolute top-full left-0 mt-2 border rounded-lg shadow-xl overflow-hidden z-50 min-w-30 ${
                  theme === 'dark'
                    ? 'bg-gray-900 border-gray-700'
                    : 'bg-white border-gray-300'
                }`}
              >
                <button
                  onClick={() => handleLanguageChange('en')}
                  className={`w-full px-4 py-2 text-left text-sm transition flex items-center gap-2 ${
                    i18n.language === 'en'
                      ? 'bg-orange-500/20 text-orange-400'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-800'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="font-medium">EN</span>
                  {t('navbar.languages.english')}
                </button>
                <button
                  onClick={() => handleLanguageChange('fa')}
                  className={`w-full px-4 py-2 text-left text-sm transition flex items-center gap-2 ${
                    i18n.language === 'fa'
                      ? 'bg-orange-500/20 text-orange-400'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-800'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="font-medium">FA</span>
                  Dari
                </button>
                <button
                  onClick={() => handleLanguageChange('ps')}
                  className={`w-full px-4 py-2 text-left text-sm transition flex items-center gap-2 ${
                    i18n.language === 'ps'
                      ? 'bg-orange-500/20 text-orange-400'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gray-800'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="font-medium">PS</span>
                  {t('navbar.languages.pashto')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6 text-sm font-medium">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`flex items-center gap-2 transition ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <item.icon className={`w-4 h-4 ${item.color}`} />
              {item.label}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {!isLoggedIn ? (
            <>
              <Link
                to="/login"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {t('navbar.login')}
              </Link>
              <Link
                to="/register"
                className="hidden sm:block px-5 py-2 rounded-lg text-sm font-semibold
                           bg-gradient-to-r from-orange-500 to-orange-600 text-white
                           hover:shadow-lg hover:shadow-orange-500/25 transition
                           shadow-[0_0_0_1px_rgba(249,115,22,0.4)]"
              >
                {t('navbar.getStarted')}
              </Link>
            </>
          ) : (
            <Link
              to="/dashboard"
              className="hidden sm:block px-5 py-2 rounded-lg text-sm font-semibold
                         bg-gradient-to-r from-orange-500 to-orange-600 text-white
                         hover:shadow-lg hover:shadow-orange-500/25 transition"
            >
              {t('navbar.dashboard')}
            </Link>
          )}

          {/* Mobile menu toggle */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className={`md:hidden p-2 rounded-lg transition ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-gray-800'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            aria-label={t('common.toggleMenu')}
          >
            {showMobileMenu ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div
            ref={mobileMenuRef}
            className={`absolute top-16 left-0 right-0 md:hidden border-b shadow-xl ${
              theme === 'dark'
                ? 'bg-[#0B0E14] border-gray-800'
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="px-6 py-4 space-y-3">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-2 text-sm font-medium transition flex items-center gap-2 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  {item.label}
                </button>
              ))}
              {!isLoggedIn && (
                <Link
                  to="/register"
                  onClick={() => setShowMobileMenu(false)}
                  className="block py-2 text-sm font-semibold bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg text-center hover:shadow-lg transition"
                >
                  {t('navbar.getStarted')}
                </Link>
              )}
              {isLoggedIn && (
                <Link
                  to="/dashboard"
                  onClick={() => setShowMobileMenu(false)}
                  className="block py-2 text-sm font-semibold bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg text-center hover:shadow-lg transition"
                >
                  {t('navbar.dashboard')}
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
